"use client";
import { AnimatePresence, motion } from "motion/react";
import { Message } from "../ai-elements/message";
import {
  AttachmentVariant,
  type PromptAddon,
  PromptAddonType,
} from "@/stores/slices/prompt-context.slice";
import type { ImagineUIMessage } from "@imagine/ai-service/shared-types";
import { ContextChips } from "@/components/prompt-composer/context-chips";
import { TruncatableUserBubble } from "./truncatable-user-bubble";
import { SUPPORTED_IMAGE_TYPES } from "../prompt-composer";

const AnimatedMessage = motion(Message);

export function UserMessage({ message }: { message: ImagineUIMessage }) {
  const addons = extractAddonsFromMessage(message);
  const textParts = message.parts.filter((part) => part.type === "text");

  return (
    <AnimatePresence>
      <AnimatedMessage
        from="user"
        key={message.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.25 }}
        className="flex-col items-end space-y-0"
      >
        {textParts.map((part, i) => (
          <TruncatableUserBubble key={`${message.id}-${i}`} text={part.text} />
        ))}

        {addons.length > 0 && (
          <div className="flex items-center flex-wrap gap-2 min-w-0 justify-end">
            <ContextChips addons={addons} />
          </div>
        )}
      </AnimatedMessage>
    </AnimatePresence>
  );
}

/**
 * Extracts addons (attachments and selected elements) from a message.
 *
 * Why we check both parts and metadata:
 * - File parts: Present in new messages before persistence
 * - metadata.files: Fallback for persisted messages (backend reconstructs file parts but double-check)
 * - metadata.selectedElements: Only location for selected elements after persistence
 *
 * The backend saves file metadata and selectedElements to metadata because
 * Mastra's experimental_attachments format loses filename and we need display props.
 */
function extractAddonsFromMessage(
  message: ImagineUIMessage,
): Array<PromptAddon> {
  const addons: Array<PromptAddon> = [];

  // Extract files from parts
  for (const part of message.parts) {
    if (part.type === "file") {
      addons.push({
        id: part.url,
        type: PromptAddonType.ATTACHMENT,
        props: {
          url: part.url,
          variant: SUPPORTED_IMAGE_TYPES.includes(part.mediaType)
            ? AttachmentVariant.IMAGE
            : AttachmentVariant.FILE,
          filename: part.filename,
          mediaType: part.mediaType,
          uploadStatus: "success",
          removable: false,
        },
      });
    }
  }

  // Extract selected elements from metadata
  if (message.metadata?.selectedElements) {
    for (const element of message.metadata.selectedElements) {
      addons.push({
        id: element.uniqueId,
        type: PromptAddonType.SELECTED_ELEMENT,
        props: {
          xpath: element.xpath,
          uniqueId: element.uniqueId,
          pathname: element.pathname,
          previewText: element.previewText,
          tagName: element.tagName,
          elementId: element.elementId,
          className: element.className,
          removable: false,
        },
      });
    }
  }

  return addons;
}
